import "./prism-retrieval.css"

import type { CSSProperties } from "react"

import { useTranslation } from "react-i18next"

import type { FilthSearchActivityState, FilthSpaceType } from "../../utils/filth-search-activity"

import {
  Building2,
  CircleCheckSolid,
  FileCheck2,
  type LucideIcon,
  UserRound,
  UsersRound,
} from "@/components/icons"
import { usePrismSequence } from "@/features/chat/hooks/use-prism-sequence"
import { usePrismSettleHeight } from "@/features/chat/hooks/use-prism-settle-height"
import { usePrismNamedBefore } from "@/features/chat/hooks/use-prism-turn"
import { cn } from "@/lib/utils"

import { PrismPanelHeader } from "./prism-panel-header"
import {
  PRISM_ACCESS_SETTLE,
  PRISM_ACCESS_STEP_MS,
  PRISM_ACCESS_TILES_MAX,
  PRISM_RESERVE_COUNT,
  PRISM_TILE_STAGGER_MS,
  PRISM_TILE_WIDTHS,
  type PrismSource,
} from "./prism-retrieval-constants"
import { getPrismBadge, getPrismPlaceholderCount, getPrismStepCap } from "./prism-retrieval-utils"
import { PrismSourceChip } from "./prism-source-chip"
import { PrismStepPills } from "./prism-step-pills"

type PrismAccessPanelProps = {
  state: FilthSearchActivityState
  sources: PrismSource[]
  isLive: boolean
  /** The answer's citation click handler; makes each named source a button. */
  onSourceClick?: (citationIndex: number) => void
}

const SPACE_ICONS: Record<FilthSpaceType, LucideIcon> = {
  company: Building2,
  shared: UsersRound,
  personal: UserRound,
}

/** One slot per space type FILTH can report. */
const SPACE_SLOTS = Object.keys(SPACE_ICONS).length

/** Holds a reserved row's line height without drawing anything. */
const NBSP = "\u00a0"

const SPACE_CLASS =
  "border-foreground bg-accent text-accent-foreground flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium"

/**
 * Behörighetspanelen: the most detailed Prism view, and the successor of the
 * FILTH search visualization. It shows the control flow, the knowledge spaces
 * the permitted evidence came from, and the reported evidence, file, claim
 * and related-link counts. Only the spaces FILTH reported are drawn: an absent
 * space says nothing about access, so it is not shown at all.
 *
 * The live turn reserves the grounded structure from mount: every space,
 * tile and count slot exists from the first frame and only changes content,
 * so grounding never pushes the answer below down. A cited source takes over
 * its tray slot in place: it is pulled into focus on top of the placeholder,
 * which recedes beneath it, and the slot keeps its size. A finished turn
 * draws only its content, and the live turn settles into exactly that layout
 * when it ends: the reserved rows close on Focus rather than at once.
 */
export function PrismAccessPanel({ state, sources, isLive, onSourceClick }: PrismAccessPanelProps) {
  const { t } = useTranslation()
  const total = PRISM_ACCESS_STEP_MS.length
  const cap = getPrismStepCap(state, total)
  const sequence = usePrismSequence(isLive, PRISM_ACCESS_STEP_MS, { cap, memoryId: "access" })
  // Named before the message remounted: already on screen, so no entrance.
  const namedBefore = usePrismNamedBefore()
  const done = Math.min(sequence.step, cap)
  const active = sequence.running && done < total ? done : -1
  // The grounded state, once the sequence has reached it; undefined before.
  const resolved = state.phase === "grounded" && done >= total ? state : undefined

  const spaceLabels: Record<FilthSpaceType, string> = {
    company: t("activity_messages.filth.company", { defaultValue: "Company" }),
    shared: t("activity_messages.filth.shared", { defaultValue: "Teams & groups" }),
    personal: t("activity_messages.filth.personal", { defaultValue: "Personal" }),
  }
  // The live structure is held while the turn is live, then released.
  const reserve = sequence.mountedLive && isLive
  // Both regions close their reserved rows in halves, one Guiding beat
  // apart, and the tiles follow the spaces two beats later: at no frame does
  // the answer below move faster than about 6 px (`PRISM_ACCESS_SETTLE`).
  const spacesRef = usePrismSettleHeight<HTMLUListElement>(
    sequence.running,
    sequence.motion,
    PRISM_ACCESS_SETTLE.spaces
  )
  const tilesRef = usePrismSettleHeight<HTMLUListElement>(
    sequence.running,
    sequence.motion,
    PRISM_ACCESS_SETTLE.tiles
  )
  const authorizing = t("activity_messages.filth.authorizing", {
    defaultValue: "Checking identity and access",
  })
  const writing = t("activity_messages.filth.writingAnswer", {
    count: state.phase === "grounded" ? state.evidenceCount : PRISM_RESERVE_COUNT,
    defaultValue: "Writing the answer · {{count}} evidence items",
  })
  const grounded = t("activity_messages.filth.grounded", {
    defaultValue: "Answer grounded in permitted evidence",
  })
  // Between grounding and the first named source, the answer is being written.
  let subtitle = authorizing
  if (resolved && sequence.running && sources.length === 0) subtitle = writing
  else if (resolved) subtitle = grounded
  const spaceTypes = resolved?.spaceTypes ?? []
  const permittedOnly = resolved !== undefined && spaceTypes.length === 0
  let spaceSlots = 0
  if (reserve) spaceSlots = SPACE_SLOTS
  else if (resolved) spaceSlots = Math.max(1, spaceTypes.length)
  const tileSources = resolved ? sources : []
  const placeholderTiles = resolved
    ? getPrismPlaceholderCount(state, sources.length, { max: PRISM_ACCESS_TILES_MAX, pending: 0 })
    : 0
  const filledTiles = tileSources.length + placeholderTiles
  const tileSlots = reserve ? Math.max(PRISM_ACCESS_TILES_MAX, filledTiles) : filledTiles
  // Keyed to the slot, never to what fills it, so a delay never re-times.
  const guideIn = (slot: number): CSSProperties | undefined =>
    sequence.entrance ? { animationDelay: `${slot * PRISM_TILE_STAGGER_MS}ms` } : undefined
  const stats = [
    {
      key: "files",
      value: resolved?.fileCount,
      label: t("activity_messages.filth.files", { defaultValue: "Files" }),
    },
    {
      key: "claims",
      value: resolved?.claimCount,
      label: t("activity_messages.filth.claims", { defaultValue: "Claims" }),
    },
    {
      key: "related",
      value: resolved?.relatedCount,
      label: t("activity_messages.filth.related", {
        count: resolved?.relatedCount ?? 0,
        defaultValue: "Related links",
      }),
    },
  ]

  return (
    <section
      aria-label={t("activity_messages.filth.accessView", { defaultValue: "Access panel" })}
      data-prism-animated={sequence.motion}
      data-testid="prism-access-panel"
      className={cn(
        "bg-card grid min-w-0 gap-3 rounded-xl border p-3.5",
        sequence.motion && "prism-motion"
      )}
    >
      <PrismPanelHeader
        badge={getPrismBadge(t, state)}
        badgeDone={resolved !== undefined}
        reserve={sequence.mountedLive ? [writing, grounded] : undefined}
        subtitle={subtitle}
      />

      <PrismStepPills
        active={active}
        connectorLength={3}
        done={done}
        label={t("activity_messages.filth.stepsLabel", { defaultValue: "Prism steps" })}
        steps={[
          {
            key: "identity",
            label: t("activity_messages.filth.steps.identity", { defaultValue: "Identity" }),
          },
          {
            key: "sourceAccess",
            label: t("activity_messages.filth.steps.sourceAccess", {
              defaultValue: "Source access",
            }),
          },
          {
            key: "spaces",
            label: t("activity_messages.filth.steps.spaces", { defaultValue: "Knowledge spaces" }),
          },
        ]}
      />

      {spaceSlots > 0 && (
        <ul
          ref={spacesRef}
          className="prism-settle grid gap-2 sm:grid-cols-3"
          aria-label={t("activity_messages.filth.spacesLabel", {
            defaultValue: "Knowledge spaces",
          })}
        >
          {Array.from({ length: spaceSlots }, (_, index) => {
            const slot = `space-${index}`
            const spaceType = spaceTypes.at(index)
            if (spaceType) {
              const Icon = SPACE_ICONS[spaceType]
              return (
                <li
                  key={slot}
                  className={cn(SPACE_CLASS, sequence.entrance && "prism-guide-in")}
                  data-prism-slot={slot}
                  data-space={spaceType}
                  style={guideIn(index)}
                >
                  <Icon aria-hidden="true" className="size-3.5 shrink-0" />
                  <span className="truncate">{spaceLabels[spaceType]}</span>
                  <CircleCheckSolid aria-hidden="true" className="ml-auto size-3.5 shrink-0" />
                </li>
              )
            }
            if (permittedOnly && index === 0) {
              // Beside reserved slots it keeps one cell, so the row never wraps.
              return (
                <li
                  key={slot}
                  data-prism-slot={slot}
                  className={cn(
                    SPACE_CLASS,
                    !reserve && "sm:col-span-3",
                    sequence.entrance && "prism-guide-in"
                  )}
                >
                  <FileCheck2 aria-hidden="true" className="size-3.5 shrink-0" />
                  <span className="truncate">
                    {t("activity_messages.filth.permittedResults", {
                      defaultValue: "Permitted results",
                    })}
                  </span>
                </li>
              )
            }
            return (
              <li
                key={slot}
                aria-hidden="true"
                className={cn(SPACE_CLASS, "invisible")}
                data-prism-slot={slot}
              >
                <span className="size-3.5 shrink-0" />
                <span>{NBSP}</span>
              </li>
            )
          })}
        </ul>
      )}

      {(reserve || resolved) && (
        <div
          className={cn(
            "prism-guide grid gap-2.5 rounded-lg p-2.5",
            resolved ? "bg-secondary" : "bg-transparent"
          )}
        >
          <ul
            ref={tilesRef}
            className="prism-settle grid min-h-6 grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-1.5"
            aria-label={t("activity_messages.filth.evidenceLabel", {
              defaultValue: "Permitted evidence",
            })}
          >
            {Array.from({ length: tileSlots }, (_, index) => {
              const slot = `tile-${index}`
              const source = tileSources.at(index)
              const filled = index < filledTiles
              // The slot is a fixed cell and never changes size or classes: a
              // cited source is pulled into focus on top of its placeholder,
              // which recedes underneath on the same beat (Focus).
              return (
                <li
                  key={slot}
                  aria-hidden={source ? undefined : true}
                  data-prism-placeholder={filled && !source ? "" : undefined}
                  data-prism-slot={slot}
                  style={guideIn(index)}
                  className={cn(
                    "relative h-6 min-w-0",
                    filled ? sequence.entrance && "prism-guide-in" : "invisible"
                  )}
                >
                  {(!source || sequence.motion) && (
                    <span
                      className="prism-shape bg-card block h-6 rounded-md border"
                      data-covered={source ? true : undefined}
                      data-prism-shape=""
                      style={{ width: PRISM_TILE_WIDTHS[index % PRISM_TILE_WIDTHS.length] }}
                    />
                  )}
                  {source && (
                    <PrismSourceChip
                      source={source}
                      className={cn(
                        "border-foreground bg-card absolute inset-0 flex min-w-0 items-center gap-1.5 rounded-md border px-2 text-[11px] font-semibold",
                        sequence.motion && index >= namedBefore && "prism-focus-pull"
                      )}
                      onOpen={onSourceClick}
                    >
                      <span className="tabular-nums">{source.n}</span>
                      <span className="text-muted-foreground truncate font-medium">
                        {source.title}
                      </span>
                    </PrismSourceChip>
                  )}
                </li>
              )
            })}
          </ul>
          <div className="flex gap-5">
            {stats.map((stat) => (
              <div
                key={stat.key}
                aria-hidden={resolved ? undefined : true}
                className={cn(resolved ? sequence.entrance && "prism-guide-in" : "invisible")}
                data-prism-slot={`stat-${stat.key}`}
                data-stat={resolved ? stat.key : undefined}
              >
                <b className="block text-lg leading-tight font-bold tabular-nums">
                  {stat.value ?? NBSP}
                </b>
                <small className="text-muted-foreground text-[11px]">{stat.label}</small>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}
